import React from 'react';
import PropTypes from 'prop-types';
import {
  AreaChart, Area, BarChart, Bar,
  CartesianGrid, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { StyledChartWrap } from './StyledCidAnalyticsDashboardPage';
import {
  CHART_TOOLTIP, AXIS_TICK, GRID_STYLE,
  REVENUE_COLORS, EST_REVENUE_COLORS, WEEKLY_COLORS, PERF_COLORS,
  formatMoney, formatMonthLabel, formatMonthShort, formatK, formatViews,
  EmptyChart,
} from './CidAnalyticsShared';

export function RevenueAreaChart({ data, loading }) {
  if (!data || data.length < 2) return <EmptyChart loading={loading} />;

  return (
    <StyledChartWrap>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{
            top: 8, right: 16, bottom: 0, left: 8,
          }}
        >
          <defs>
            <linearGradient id="gradPartner" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={REVENUE_COLORS.totalPartnerRevenue} stopOpacity={0.2} />
              <stop offset="95%" stopColor={REVENUE_COLORS.totalPartnerRevenue} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradRedflag" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={REVENUE_COLORS.redflagShare} stopOpacity={0.2} />
              <stop offset="95%" stopColor={REVENUE_COLORS.redflagShare} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradPayable" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={REVENUE_COLORS.payableRevenue} stopOpacity={0.2} />
              <stop offset="95%" stopColor={REVENUE_COLORS.payableRevenue} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis dataKey="month" tickFormatter={formatMonthShort} tick={AXIS_TICK} tickLine={false} axisLine={false} />
          <YAxis tickFormatter={formatK} tick={AXIS_TICK} tickLine={false} axisLine={false} width={60} />
          <Tooltip
            {...CHART_TOOLTIP}
            labelFormatter={formatMonthLabel}
            formatter={(value, name) => {
              const labels = {
                totalPartnerRevenue: 'Partner Revenue',
                redflagShare: 'Redflag Earnings',
                payableRevenue: 'Payable Revenue',
              };
              return [formatMoney(value), labels[name] || name];
            }}
          />
          <Legend
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ fontSize: '0.85rem', paddingTop: '0.5rem', color: 'var(--page-secondary-text)' }}
            formatter={(value) => {
              const labels = {
                totalPartnerRevenue: 'Partner Revenue',
                redflagShare: 'Redflag Earnings',
                payableRevenue: 'Payable Revenue',
              };
              return labels[value] || value;
            }}
          />
          <Area type="monotone" dataKey="totalPartnerRevenue" stroke={REVENUE_COLORS.totalPartnerRevenue} strokeWidth={2} fill="url(#gradPartner)" dot={false} activeDot={{ r: 4 }} />
          <Area type="monotone" dataKey="redflagShare" stroke={REVENUE_COLORS.redflagShare} strokeWidth={2} fill="url(#gradRedflag)" dot={false} activeDot={{ r: 4 }} />
          <Area type="monotone" dataKey="payableRevenue" stroke={REVENUE_COLORS.payableRevenue} strokeWidth={2} fill="url(#gradPayable)" dot={false} activeDot={{ r: 4 }} />
        </AreaChart>
      </ResponsiveContainer>
    </StyledChartWrap>
  );
}

RevenueAreaChart.propTypes = { data: PropTypes.array, loading: PropTypes.bool };

export function EstimatedRevenueAreaChart({ data, loading }) {
  if (!data || data.length < 2) return <EmptyChart loading={loading} />;

  return (
    <StyledChartWrap>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{
            top: 8, right: 16, bottom: 0, left: 8,
          }}
        >
          <defs>
            <linearGradient id="gradEstTotal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={EST_REVENUE_COLORS.totalEstimatedRevenue} stopOpacity={0.2} />
              <stop offset="95%" stopColor={EST_REVENUE_COLORS.totalEstimatedRevenue} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradEstRedflag" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={EST_REVENUE_COLORS.estimatedRedflagShare} stopOpacity={0.2} />
              <stop offset="95%" stopColor={EST_REVENUE_COLORS.estimatedRedflagShare} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gradEstPayable" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={EST_REVENUE_COLORS.estimatedPayableRevenue} stopOpacity={0.2} />
              <stop offset="95%" stopColor={EST_REVENUE_COLORS.estimatedPayableRevenue} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis dataKey="month" tickFormatter={formatMonthShort} tick={AXIS_TICK} tickLine={false} axisLine={false} />
          <YAxis tickFormatter={formatK} tick={AXIS_TICK} tickLine={false} axisLine={false} width={60} />
          <Tooltip
            {...CHART_TOOLTIP}
            labelFormatter={formatMonthLabel}
            formatter={(value, name) => {
              const labels = {
                totalEstimatedRevenue: 'Est. Total Revenue',
                estimatedRedflagShare: 'Est. Redflag Share',
                estimatedPayableRevenue: 'Est. Payable Revenue',
              };
              return [formatMoney(value), labels[name] || name];
            }}
          />
          <Legend
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ fontSize: '0.85rem', paddingTop: '0.5rem', color: 'var(--page-secondary-text)' }}
            formatter={(value) => {
              const labels = {
                totalEstimatedRevenue: 'Est. Total Revenue',
                estimatedRedflagShare: 'Est. Redflag Share',
                estimatedPayableRevenue: 'Est. Payable Revenue',
              };
              return labels[value] || value;
            }}
          />
          <Area type="monotone" dataKey="totalEstimatedRevenue" stroke={EST_REVENUE_COLORS.totalEstimatedRevenue} strokeWidth={2} fill="url(#gradEstTotal)" dot={false} activeDot={{ r: 4 }} />
          <Area type="monotone" dataKey="estimatedRedflagShare" stroke={EST_REVENUE_COLORS.estimatedRedflagShare} strokeWidth={2} fill="url(#gradEstRedflag)" dot={false} activeDot={{ r: 4 }} />
          <Area type="monotone" dataKey="estimatedPayableRevenue" stroke={EST_REVENUE_COLORS.estimatedPayableRevenue} strokeWidth={2} fill="url(#gradEstPayable)" dot={false} activeDot={{ r: 4 }} />
        </AreaChart>
      </ResponsiveContainer>
    </StyledChartWrap>
  );
}

EstimatedRevenueAreaChart.propTypes = { data: PropTypes.array, loading: PropTypes.bool };

export function CreatorGrowthChart({ data, loading }) {
  if (!data || data.length === 0) return <EmptyChart loading={loading} />;

  return (
    <StyledChartWrap>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{
            top: 8, right: 16, bottom: 0, left: 8,
          }}
        >
          <CartesianGrid {...GRID_STYLE} vertical={false} />
          <XAxis dataKey="month" tickFormatter={formatMonthShort} tick={AXIS_TICK} tickLine={false} axisLine={false} />
          <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} width={32} />
          <Tooltip
            {...CHART_TOOLTIP}
            labelFormatter={formatMonthLabel}
            formatter={(value) => [value, 'New Creators']}
          />
          <Bar dataKey="newCreators" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={36} />
        </BarChart>
      </ResponsiveContainer>
    </StyledChartWrap>
  );
}

CreatorGrowthChart.propTypes = { data: PropTypes.array, loading: PropTypes.bool };

export function WeeklyGrowthChart({ data, loading }) {
  if (!data || data.length === 0) return <EmptyChart loading={loading} />;
  return (
    <StyledChartWrap>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{
            top: 8, right: 48, bottom: 0, left: 8,
          }}
        >
          <CartesianGrid {...GRID_STYLE} vertical={false} />
          <XAxis dataKey="week" tick={AXIS_TICK} tickLine={false} axisLine={false} />
          <YAxis
            yAxisId="assets"
            orientation="left"
            allowDecimals={false}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={40}
            label={{
              value: 'Assets', angle: -90, position: 'insideLeft', fill: WEEKLY_COLORS.newAssets, fontSize: 11,
            }}
          />
          <YAxis
            yAxisId="creators"
            orientation="right"
            allowDecimals={false}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={40}
            label={{
              value: 'Creators', angle: 90, position: 'insideRight', fill: WEEKLY_COLORS.newCreators, fontSize: 11,
            }}
          />
          <Tooltip
            {...CHART_TOOLTIP}
            formatter={(value, name) => [value, name === 'newCreators' ? 'New Creators' : 'New Assets']}
          />
          <Legend
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ fontSize: '0.85rem', paddingTop: '0.5rem' }}
            formatter={(v) => (v === 'newCreators' ? 'New Creators' : 'New Assets')}
          />
          <Bar yAxisId="assets" dataKey="newAssets" fill={WEEKLY_COLORS.newAssets} radius={[4, 4, 0, 0]} maxBarSize={24} />
          <Bar yAxisId="creators" dataKey="newCreators" fill={WEEKLY_COLORS.newCreators} radius={[4, 4, 0, 0]} maxBarSize={24} />
        </BarChart>
      </ResponsiveContainer>
    </StyledChartWrap>
  );
}

WeeklyGrowthChart.propTypes = { data: PropTypes.array, loading: PropTypes.bool };

export function WeeklyViewsChart({ data, loading }) {
  if (!data || data.length < 2) return <EmptyChart loading={loading} />;
  return (
    <StyledChartWrap>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{
            top: 8, right: 16, bottom: 0, left: 8,
          }}
        >
          <defs>
            <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={WEEKLY_COLORS.engagedViews} stopOpacity={0.25} />
              <stop offset="95%" stopColor={WEEKLY_COLORS.engagedViews} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis dataKey="week" tick={AXIS_TICK} tickLine={false} axisLine={false} />
          <YAxis tickFormatter={formatViews} tick={AXIS_TICK} tickLine={false} axisLine={false} width={52} />
          <Tooltip
            {...CHART_TOOLTIP}
            formatter={(value) => [formatViews(value), 'Engaged Views']}
          />
          <Area type="monotone" dataKey="engagedViews" stroke={WEEKLY_COLORS.engagedViews} strokeWidth={2} fill="url(#gradViews)" dot={false} activeDot={{ r: 4 }} />
        </AreaChart>
      </ResponsiveContainer>
    </StyledChartWrap>
  );
}

WeeklyViewsChart.propTypes = { data: PropTypes.array, loading: PropTypes.bool };

export function RpmByMonthChart({ data, loading }) {
  if (!data || data.length < 2) return <EmptyChart loading={loading} />;
  return (
    <StyledChartWrap>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{
            top: 8, right: 16, bottom: 0, left: 8,
          }}
        >
          <defs>
            <linearGradient id="gradRpm" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={PERF_COLORS.rpm} stopOpacity={0.25} />
              <stop offset="95%" stopColor={PERF_COLORS.rpm} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis dataKey="month" tickFormatter={formatMonthShort} tick={AXIS_TICK} tickLine={false} axisLine={false} />
          <YAxis tickFormatter={(v) => `$${v}`} tick={AXIS_TICK} tickLine={false} axisLine={false} width={48} />
          <Tooltip
            {...CHART_TOOLTIP}
            labelFormatter={formatMonthLabel}
            formatter={(value) => [`$${value}`, 'RPM']}
          />
          <Area type="monotone" dataKey="rpm" stroke={PERF_COLORS.rpm} strokeWidth={2} fill="url(#gradRpm)" dot={false} activeDot={{ r: 4 }} />
        </AreaChart>
      </ResponsiveContainer>
    </StyledChartWrap>
  );
}

RpmByMonthChart.propTypes = { data: PropTypes.array, loading: PropTypes.bool };
